import { useCallback, useEffect, useMemo, useState } from "react";
import { Plus, RefreshCcw, Trash2, Wand2 } from "lucide-react";

import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import api from "@/lib/api";
import { money } from "@/lib/format";
import { toast } from "sonner";
import { getApiErrorMessage } from "@/lib/apiError";

const CATEGORIES = [
    { key: "options", title: "Options & Upgrades", description: "Home options the customer added" },
    { key: "mandatory_services", title: "Site Work", description: "Recommended setup, utilities, permits, and project work" },
    { key: "addons", title: "Add-Ons", description: "Optional customer upgrades and add-ons" },
    { key: "site_work", title: "Delivery", description: "Delivery, freight, transport, and escort related charges" },
];

const EMPTY_TEMPLATE_GROUPS = {
    mandatory_services: [],
    addons: [],
    site_work: [],
};

const DEFAULT_AUTO_SERVICES = [
    "Block & Tie-Down",
    "Trim Out",
    "Electric Pole & Panel",
    "Electric Hookups",
    "Legal + Impact Fees",
    "AC Unit & Installation",
    "Perimeter Stabilization",
    "Dirt Pad",
];

const DEFAULT_SERVICE_IDS = new Set(["SITE-PERMIT-PLAN", "SITE-STEPS-2SET"]);

const PERMIT_TEMPLATE_NAMES = [
    "Permit",
    "Permits",
    "Permit Fee",
    "Building Permit",
    "Setup Permit",
    "State Permit",
    "County Permit",
    "Mobile Home Permit",
];

const STEPS_TEMPLATE_NAMES = [
    "Steps",
    "Front Steps",
    "Rear Steps",
    "Steps / Landing",
    "Stairs",
    "Entry Steps",
    "Code Steps",
];

const AC_PRICING = {
    "2|straight_cool": { packageCost: 4500, packagePrice: 4950, splitCost: 3800, splitPrice: 4180 },
    "2|heat_pump": { packageCost: 4700, packagePrice: 5170, splitCost: 4050, splitPrice: 4455 },
    "2.5|straight_cool": { packageCost: 4600, packagePrice: 5060, splitCost: 4100, splitPrice: 4510 },
    "2.5|heat_pump": { packageCost: 4800, packagePrice: 5280, splitCost: 4300, splitPrice: 4730 },
    "3|straight_cool": { packageCost: 4750, packagePrice: 5225, splitCost: 4500, splitPrice: 4950 },
    "3|heat_pump": { packageCost: 5050, packagePrice: 5555, splitCost: 4700, splitPrice: 5170 },
    "3.5|straight_cool": { packageCost: 4950, packagePrice: 5445, splitCost: 4900, splitPrice: 5390 },
    "3.5|heat_pump": { packageCost: 5200, packagePrice: 5720, splitCost: 5100, splitPrice: 5610 },
    "4|straight_cool": { packageCost: 5150, packagePrice: 5665, splitCost: 5300, splitPrice: 5830 },
    "4|heat_pump": { packageCost: 5500, packagePrice: 6050, splitCost: 5550, splitPrice: 6105 },
    "5|straight_cool": { packageCost: 6000, packagePrice: 6600, splitCost: 5750, splitPrice: 6325 },
    "5|heat_pump": { packageCost: 6500, packagePrice: 7150, splitCost: 5950, splitPrice: 6545 },
};

const BLOCK_LENGTHS = [30, 36, 40, 44, 48, 52, 56, 60, 66, 72, 76, 80];
const BLOCK_TIE_DOWN_TABLE = {
    single: {
        cost: [3318, 3588, 3858, 4128, 4398, 4668, 6538, 6708, 6978, 7148, 7418, 7588],
        price: [4147.5, 4485, 4822.5, 5160, 5497.5, 5835, 8172.5, 8385, 8722.5, 8935, 9272.5, 9485],
    },
    double: {
        cost: [5218, 5704, 6302, 6788, 7386, 7872, 9270, 9756, 10354, 10760, 11358, 11844],
        price: [6522.5, 7130, 7877.5, 8485, 9232.5, 9840, 11587.5, 12195, 12942.5, 13450, 14197.5, 14805],
    },
    triple: {
        cost: [6261.6, 6844.8, 7562.4, 8145.6, 8863.2, 9446.4, 11124, 11707.2, 12424.8, 12912, 13629.6, 14212.8],
        price: [7827, 8556, 9453, 10182, 11079, 11808, 13905, 14634, 15531, 16140, 17037, 17766],
    },
};

const DIRT_PAD_LOAD_TABLE = [
    { loads: 1, cost: 1000, price: 1500 },
    { loads: 2, cost: 1800, price: 2700 },
    { loads: 3, cost: 2400, price: 3600 },
    { loads: 4, cost: 2900, price: 4350 },
    { loads: 5, cost: 3400, price: 5100 },
    { loads: 6, cost: 3900, price: 5850 },
    { loads: 7, cost: 4400, price: 6600 },
    { loads: 8, cost: 4900, price: 7350 },
    { loads: 9, cost: 5400, price: 8100 },
    { loads: 10, cost: 5900, price: 8850 },
    { loads: 11, cost: 6400, price: 9600 },
    { loads: 12, cost: 6900, price: 10350 },
    { loads: 13, cost: 7400, price: 11100 },
    { loads: 14, cost: 7900, price: 11850 },
    { loads: 15, cost: 8400, price: 12600 },
    { loads: 16, cost: 8900, price: 13350 },
    { loads: 17, cost: 9400, price: 14100 },
    { loads: 18, cost: 9900, price: 14850 },
    { loads: 19, cost: 10400, price: 15600 },
    { loads: 20, cost: 10900, price: 16350 },
];

function templatePriceLabel(template) {
    const price = Number(template?.default_unit_price || template?.unit_price || template?.default_price || template?.price || 0);
    if (template?.requires_bid || template?.calc_type === "bid_required") return "BID";
    return price > 0 ? money(price) : "TBD";
}

function templateCost(template) {
    return Number(template?.default_cost || template?.cost || 0);
}

function templatePrice(template) {
    if (template?.requires_bid || template?.calc_type === "bid_required") return 0;
    return Number(template?.default_unit_price || template?.unit_price || template?.default_price || template?.price || 0);
}

function normalizeText(value) {
    return String(value || "").trim().toLowerCase();
}

function normalizeSearchText(value) {
    return normalizeText(value).replace(/[^a-z0-9]+/g, " ").trim();
}

function compactSearchText(value) {
    return normalizeSearchText(value).replace(/\s+/g, "");
}

function templateSearchText(template) {
    return normalizeSearchText([
        template?.description,
        template?.name,
        template?.service_name,
        template?.service_id,
        template?.sku,
        template?.id,
        template?.template_id,
        template?.service_catalog_category,
        template?.catalog_category,
    ].filter(Boolean).join(" "));
}

function itemSearchText(item) {
    return normalizeSearchText([
        item?.description,
        item?.service_id,
        item?.catalog_template_id,
        item?.catalog_category,
        item?.calc_type,
    ].filter(Boolean).join(" "));
}

function serviceIdentity(value) {
    return compactSearchText(value);
}

function templateIdentity(template) {
    return serviceIdentity(template?.service_id || template?.id || template?.template_id || template?.description || template?.name);
}

function itemIdentity(item) {
    return serviceIdentity(item?.service_id || item?.catalog_template_id || item?.description);
}

function exactNameMatch(text, names) {
    const compactText = compactSearchText(text);
    return names.some((name) => compactText === compactSearchText(name));
}

function phraseMatch(text, names) {
    const paddedText = ` ${normalizeSearchText(text)} `;
    return names.some((name) => paddedText.includes(` ${normalizeSearchText(name)} `));
}

function isPermitTemplate(template) {
    const category = normalizeText(template?.service_catalog_category || template?.catalog_category);
    const searchText = templateSearchText(template);
    return category === "permit_fee" || phraseMatch(searchText, PERMIT_TEMPLATE_NAMES) || searchText.includes("permit");
}

function permitTemplateScore(template) {
    const category = normalizeText(template?.service_catalog_category || template?.catalog_category);
    const name = template?.description || template?.name || template?.service_name || "";
    const searchText = templateSearchText(template);
    if (category === "permit_fee") return 0;
    if (exactNameMatch(name, PERMIT_TEMPLATE_NAMES)) return 1;
    if (phraseMatch(searchText, PERMIT_TEMPLATE_NAMES)) return 2;
    if (searchText.includes("permit")) return 3;
    return 99;
}

function isPermitLine(item) {
    const category = normalizeText(item?.catalog_category);
    const searchText = itemSearchText(item);
    return category === "permit_fee" || phraseMatch(searchText, PERMIT_TEMPLATE_NAMES) || searchText.includes("permit");
}

function isStepsTemplate(template) {
    const searchText = templateSearchText(template);
    return phraseMatch(searchText, STEPS_TEMPLATE_NAMES) || /\bsteps?\b/.test(searchText) || /\bstairs?\b/.test(searchText);
}

function stepsTemplateScore(template) {
    const name = template?.description || template?.name || template?.service_name || "";
    const searchText = templateSearchText(template);
    if (exactNameMatch(name, ["Front Steps", "Rear Steps"])) return 0;
    if (exactNameMatch(name, ["Steps", "Stairs"])) return 1;
    if (exactNameMatch(name, STEPS_TEMPLATE_NAMES)) return 2;
    if (phraseMatch(searchText, STEPS_TEMPLATE_NAMES)) return 3;
    if (/\bsteps?\b/.test(searchText) || /\bstairs?\b/.test(searchText)) return 4;
    return 99;
}

function stepsVariant(value) {
    const searchText = typeof value === "string" ? normalizeSearchText(value) : templateSearchText(value);
    if (/\bfront\b/.test(searchText)) return "front";
    if (/\brear\b|\bback\b/.test(searchText)) return "rear";
    return "generic";
}

function isStepsLine(item) {
    const searchText = itemSearchText(item);
    return phraseMatch(searchText, STEPS_TEMPLATE_NAMES) || /\bsteps?\b/.test(searchText) || /\bstairs?\b/.test(searchText);
}

function allTemplates(templates) {
    return [...(templates.mandatory_services || []), ...(templates.addons || []), ...(templates.site_work || [])];
}

function uniqueTemplates(items) {
    const seen = new Set();
    return (items || []).filter((template) => {
        const identity = templateIdentity(template);
        if (!identity || seen.has(identity)) return false;
        seen.add(identity);
        return true;
    });
}

function findPermitTemplate(templates) {
    return uniqueTemplates(allTemplates(templates))
        .filter(isPermitTemplate)
        .sort((a, b) => permitTemplateScore(a) - permitTemplateScore(b) || (a.description || a.name || "").localeCompare(b.description || b.name || ""))[0];
}

function findStepTemplates(templates) {
    const matches = uniqueTemplates(allTemplates(templates))
        .filter(isStepsTemplate)
        .sort((a, b) => stepsTemplateScore(a) - stepsTemplateScore(b) || (a.description || a.name || "").localeCompare(b.description || b.name || ""));
    const front = matches.find((template) => stepsVariant(template) === "front");
    const rear = matches.find((template) => stepsVariant(template) === "rear");
    if (front && rear) return [front, rear];
    return matches.length > 0 ? [matches[0]] : [];
}

function existingLineItems(groups) {
    return [...(groups.mandatory_services || []), ...(groups.addons || []), ...(groups.site_work || [])];
}

function hasMatchingLineItem(groups, template, family = null) {
    const templateKey = templateIdentity(template);
    const templateDescription = normalizeSearchText(template?.description || template?.name);
    return existingLineItems(groups).some((item) => {
        const itemKey = itemIdentity(item);
        if (templateKey && itemKey && templateKey === itemKey) return true;
        if (templateDescription && normalizeSearchText(item.description) === templateDescription) return true;
        if (family === "permit") return isPermitLine(item);
        if (family === "steps") {
            if (!isStepsLine(item)) return false;
            const templateVariant = stepsVariant(template);
            const itemVariant = stepsVariant(item.description || item.service_id || item.catalog_template_id || "");
            return templateVariant === "generic" || itemVariant === "generic" || templateVariant === itemVariant;
        }
        return false;
    });
}

function prettyLabel(value) {
    return String(value || "")
        .replace(/_/g, " ")
        .replace(/\b\w/g, (char) => char.toUpperCase());
}

function parseHomeDimensions(dimensions) {
    if (!dimensions) return null;

    const text = String(dimensions)
        .toLowerCase()
        .replace(/[\u2032\u2019']/g, "")
        .replace(/[\u2033\u201d"]/g, "")
        .replace(/ft\.?/g, "")
        .replace(/feet/g, "");

    const match = text.match(/(\d+(?:\.\d+)?)\s*(?:x|×|by)\s*(\d+(?:\.\d+)?)/i);
    if (!match) return null;

    const first = Number(match[1]);
    const second = Number(match[2]);
    if (!Number.isFinite(first) || !Number.isFinite(second) || first <= 0 || second <= 0) return null;

    return {
        width: Math.min(first, second),
        length: Math.max(first, second),
    };
}

function roundMoney(value) {
    const numeric = Number(value || 0);
    if (!Number.isFinite(numeric)) return 0;
    return Math.round(numeric * 100) / 100;
}

function roundQuantity(value) {
    if (!Number.isFinite(value) || value <= 0) return 1;
    return Math.round(value * 100) / 100;
}

function homePerimeter(home) {
    const dims = parseHomeDimensions(home?.dimensions);
    if (!dims) return 1;
    return roundQuantity((dims.width + dims.length) * 2);
}

function homeLength(home) {
    const dims = parseHomeDimensions(home?.dimensions);
    if (!dims) return 1;
    return roundQuantity(dims.length);
}

function homeLongSides(home) {
    const dims = parseHomeDimensions(home?.dimensions);
    if (!dims) return 1;
    return roundQuantity(dims.length * 2);
}

function inferHomeClass(home = {}) {
    const type = normalizeText(home.home_type || home.type || "");
    if (type.includes("triple")) return "triple";
    if (type.includes("multi") || type.includes("double")) return "double";

    const dims = parseHomeDimensions(home.dimensions);
    if (dims?.width >= 38) return "triple";
    if (dims?.width >= 22) return "double";
    return "single";
}

function inferTransportSides(home = {}, site = {}) {
    const explicit = Number(site.transport_sides || 0);
    if (Number.isFinite(explicit) && explicit > 0) return explicit;

    const floors = Number(home.floors || home.sections || 0);
    if (Number.isFinite(floors) && floors > 0) return floors;

    const homeClass = inferHomeClass(home);
    if (homeClass === "triple") return 3;
    if (homeClass === "double") return 2;
    return 1;
}

function estimateAcTonnage(home, site = {}) {
    const override = Number(site.ac_tonnage_override || 0);
    if (Number.isFinite(override) && override > 0) return Math.min(5, Math.max(2, Math.round(override * 2) / 2));

    const sqft = Number(home?.sqft || 0);
    if (!Number.isFinite(sqft) || sqft <= 0) return 2;

    const needed = Math.round((sqft / 500) * 2) / 2;
    return Math.min(5, Math.max(2, needed));
}

function calculateAcPricing(home, site = {}) {
    const tonnage = estimateAcTonnage(home, site);
    const systemType = site.ac_system_type || "straight_cool";
    const acType = site.ac_type || "package";
    const row = AC_PRICING[`${tonnage}|${systemType}`] || AC_PRICING[`${tonnage}|straight_cool`] || AC_PRICING["3|straight_cool"];

    if (acType === "split") {
        return { qty: 1, cost: row.splitCost, price: row.splitPrice, tonnage, systemType, acType };
    }

    return { qty: 1, cost: row.packageCost, price: row.packagePrice, tonnage, systemType, acType: "package" };
}

function xlookupNextGreaterIndex(value, table = BLOCK_LENGTHS) {
    const numeric = Number(value || 0);
    if (!Number.isFinite(numeric) || numeric <= 0) return 0;
    const index = table.findIndex((length) => numeric <= length);
    return index >= 0 ? index : table.length - 1;
}

function calculateBlockTieDownPricing(home) {
    const homeClass = inferHomeClass(home);
    const length = homeLength(home);
    const index = xlookupNextGreaterIndex(length);
    const table = BLOCK_TIE_DOWN_TABLE[homeClass] || BLOCK_TIE_DOWN_TABLE.single;

    return {
        qty: 1,
        cost: roundMoney(table.cost[index] || 0),
        price: roundMoney(table.price[index] || 0),
        matchedLength: BLOCK_LENGTHS[index],
        homeClass,
    };
}

function dirtPadLoadCount(site = {}) {
    const raw = Number(site.dirt_pad_loads || 2);
    if (!Number.isFinite(raw) || raw <= 0) return 2;
    return Math.round(raw);
}

function calculateDirtPadPricing(site = {}) {
    if (site.dirt_pad_needed === false) {
        return {
            qty: 1,
            cost: 0,
            price: 0,
            loads: 0,
            requiresBid: false,
            matchedLabel: "not included",
        };
    }

    const manualCost = Number(site.dirt_pad_cost_override || 0);
    const manualPrice = Number(site.dirt_pad_price_override || 0);
    const loads = dirtPadLoadCount(site);

    if (manualCost > 0 || manualPrice > 0) {
        return {
            qty: 1,
            cost: roundMoney(manualCost),
            price: roundMoney(manualPrice),
            loads,
            requiresBid: manualPrice <= 0,
            matchedLabel: "manual override",
        };
    }

    const tableRow = DIRT_PAD_LOAD_TABLE.find((row) => row.loads === loads);
    if (!tableRow) {
        return {
            qty: 1,
            cost: 0,
            price: 0,
            loads,
            requiresBid: true,
            matchedLabel: "outside V05 1-20 load table",
        };
    }

    return {
        qty: 1,
        cost: tableRow.cost,
        price: tableRow.price,
        loads,
        requiresBid: false,
        matchedLabel: `${loads} load V05 table`,
    };
}

function deliveryMilesForRoute(site = {}, routeType) {
    if (routeType === "factory_to_dealer") return Number(site.factory_to_dealer_miles || site.delivery_miles || 0);
    if (routeType === "factory_to_customer") {
        const explicit = Number(site.factory_to_customer_miles || 0);
        if (Number.isFinite(explicit) && explicit > 0) return explicit;
        return Number(site.factory_to_dealer_miles || 0) + Number(site.dealer_to_customer_miles || site.delivery_miles || 0);
    }
    return Number(site.dealer_to_customer_miles || site.delivery_miles || 0);
}

function calculateDeliveryPricing(home, site = {}, routeOverride = null) {
    const routeType = routeOverride || site.delivery_route_type || "dealer_to_customer";
    const sides = inferTransportSides(home, site);
    const escorts = Number.isFinite(Number(site.escort_count)) && Number(site.escort_count) > 0 ? Number(site.escort_count) : sides;
    const miles = deliveryMilesForRoute(site, routeType);
    const origin = site.home_origin || "outside_florida";

    if (routeType !== "dealer_to_customer" && origin !== "florida") {
        const costPerSide = 6000;
        const pricePerSide = costPerSide * 1.1;
        return {
            qty: 1,
            cost: roundMoney(costPerSide * sides),
            price: roundMoney(pricePerSide * sides),
            miles,
            sides,
            escorts,
            routeType,
            costPerSide,
            pricePerSide,
        };
    }

    const billableMilesOver50 = Math.max(0, miles - 50);
    const costPerSide = 800 + 250 * escorts + billableMilesOver50 * 8.5 + billableMilesOver50 * 2 * escorts;
    const pricePerSide = costPerSide * 1.1;

    return {
        qty: 1,
        cost: roundMoney(costPerSide * sides),
        price: roundMoney(pricePerSide * sides),
        miles,
        sides,
        escorts,
        routeType,
        costPerSide,
        pricePerSide,
    };
}

function serviceKind(template) {
    const name = normalizeText(template?.description || template?.name || template?.service_name);
    const serviceId = normalizeText(template?.service_id || template?.sku || template?.id || template?.template_id || "");
    if (serviceId.includes("site-dirt") || (name.includes("dirt") && name.includes("pad"))) return "dirt_pad";
    if (name.includes("ac unit") || name.includes("hvac") || serviceId.startsWith("ac-")) return "ac";
    if (name.includes("block") && name.includes("tie")) return "block_tie_down";
    if (name === "freight" || name.includes("factory to dealership") || name.includes("manufacturer to dealership") || serviceId.includes("freg-zone")) return "freight";
    if (name === "delivery" || name.includes("dealership to customer") || serviceId.includes("setu-delv")) return "delivery";
    return "standard";
}

function defaultTemplateQuantity(template, home, site = {}) {
    const calcType = normalizeText(template?.calc_type || template?.calcType);
    const defaultQty = template?.default_qty ?? template?.defaultQuantity;
    const description = normalizeText(template?.description || template?.name || template?.service_name);
    const serviceId = normalizeText(template?.service_id || template?.id || template?.template_id);
    const pricingType = normalizeText(template?.pricing_type || template?.pricingType);

    if (["ac", "block_tie_down", "delivery", "freight", "dirt_pad"].includes(serviceKind(template))) return 1;

    if (
        calcType === "linear_ft" ||
        calcType === "home_perimeter" ||
        serviceId.includes("skrt") ||
        description === "skirting" ||
        description.includes("skirting")
    ) {
        return homePerimeter(home);
    }

    if (serviceId.includes("gutt") || description.includes("gutters")) {
        return homeLongSides(home);
    }

    if (calcType === "home_length") return homeLength(home);

    if (calcType === "delivery_miles") {
        const miles = Number(site.delivery_miles || 0);
        return Number.isFinite(miles) && miles > 0 ? miles : 1;
    }

    if (defaultQty !== undefined && defaultQty !== null && defaultQty !== "" && String(defaultQty).toUpperCase() !== "AUTO") {
        const numericQty = Number(defaultQty);
        return Number.isFinite(numericQty) && numericQty > 0 ? numericQty : 1;
    }

    if (pricingType.includes("scale")) return 1;

    return 1;
}

function quoteCategoryForCatalogCategory(category) {
    if (["required_service", "utility", "permit_fee", "site_work"].includes(category)) return "mandatory_services";
    if (["delivery"].includes(category)) return "site_work";
    if (category === "addon") return "addons";
    return null;
}

function normalizeServiceCatalogTemplate(item) {
    const quoteCategory = quoteCategoryForCatalogCategory(item.category);
    if (!quoteCategory) return null;
    const serviceId = item.service_id || item.sku || item.id;

    return {
        id: item.id || serviceId || item.service_name,
        service_id: serviceId,
        source: "service_catalog",
        quote_category: quoteCategory,
        category: quoteCategory,
        service_catalog_category: item.category,
        description: item.service_name || item.description || "Service item",
        name: item.service_name || item.description || "Service item",
        default_qty: item.default_qty,
        default_unit_price: Number(item.price || 0),
        default_cost: Number(item.cost || 0),
        unit: item.unit,
        vendor: item.vendor || "",
        calc_type: item.calc_type,
        requires_bid: Boolean(item.requires_bid),
        notes: item.notes,
        sort_order: Number(item.sort_order || 0),
    };
}

function normalizeLegacyTemplate(item) {
    if (!item?.category || !EMPTY_TEMPLATE_GROUPS[item.category]) return null;

    return {
        ...item,
        source: "legacy_templates",
        quote_category: item.category,
        description: item.description || item.name || "Template item",
        name: item.name || item.description || "Template item",
        default_unit_price: Number(item.default_unit_price || item.unit_price || item.default_price || 0),
        default_cost: Number(item.default_cost || item.cost || 0),
    };
}

function groupTemplates(items) {
    const grouped = { mandatory_services: [], addons: [], site_work: [] };

    for (const item of items || []) {
        if (item.active === false) continue;
        const key = item.quote_category || item.category;
        if (grouped[key]) grouped[key].push(item);
    }

    for (const key of Object.keys(grouped)) {
        grouped[key].sort((a, b) => {
            const sortA = Number(a.sort_order || 0);
            const sortB = Number(b.sort_order || 0);
            if (sortA !== sortB) return sortA - sortB;
            return (a.description || a.name || "").localeCompare(b.description || b.name || "");
        });
    }

    return grouped;
}

function findTemplate(templates, names) {
    const targets = names.map(normalizeText);
    const all = [...(templates.mandatory_services || []), ...(templates.addons || []), ...(templates.site_work || [])];
    return all.find((template) => {
        const name = normalizeText(template.description || template.name);
        const serviceId = normalizeText(template.service_id || template.id || "");
        return targets.some((target) => name === target || name.includes(target) || serviceId.includes(target.replace(/\s+/g, "-")));
    });
}

function calculatedLinePricing(template, home, site) {
    const kind = serviceKind(template);
    if (kind === "ac") {
        const result = calculateAcPricing(home, site);
        return {
            ...result,
            descriptionSuffix: `${result.tonnage} ton · ${prettyLabel(result.acType)} · ${prettyLabel(result.systemType)}`,
            calcNote: "V05 AC table: SKU price selected by tonnage, package/split, and straight cool/heat pump.",
            pricingSource: "V05 AC catalog",
        };
    }
    if (kind === "block_tie_down") {
        const result = calculateBlockTieDownPricing(home);
        return {
            ...result,
            descriptionSuffix: `${prettyLabel(result.homeClass)} · ${result.matchedLength}' table`,
            calcNote: "Block/tie-down table: exact/next-greater home length with single/double/triple pricing.",
            pricingSource: "Current CRM block/tie table",
        };
    }
    if (kind === "dirt_pad") {
        const result = calculateDirtPadPricing(site);
        return {
            ...result,
            descriptionSuffix: result.loads > 0 ? `${result.loads} load(s) · ${result.matchedLabel}` : "not included",
            calcNote: result.requiresBid
                ? "Dirt Pad requires bid: selected load count is outside the confirmed V05 1-20 load table or missing a retail override."
                : "V05 Dirt Pad table: Service Pricing Matrices 23 Cal!A10:E30, SITE-DIRT, load-based retail/cost.",
            pricingSource: "V05 SITE-DIRT load table",
        };
    }
    if (kind === "freight") {
        const result = calculateDeliveryPricing(home, site, "factory_to_dealer");
        return {
            ...result,
            descriptionSuffix: `${prettyLabel(result.routeType)} · ${result.miles || 0} mi · ${result.sides} side(s)`,
            calcNote: "Freight table: factory route, 800 base + 250/escort + over-50-mile truck/escort charges, or out-of-state factory flat rate.",
            pricingSource: "Current CRM delivery/freight calculator",
        };
    }
    if (kind === "delivery") {
        const route = site.delivery_route_type === "factory_to_customer" ? "factory_to_customer" : "dealer_to_customer";
        const result = calculateDeliveryPricing(home, site, route);
        return {
            ...result,
            descriptionSuffix: `${prettyLabel(result.routeType)} · ${result.miles || 0} mi · ${result.sides} side(s)`,
            calcNote: "Delivery table: 800 base + 250/escort + over-50-mile truck/escort charges per side.",
            pricingSource: "Current CRM delivery/freight calculator",
        };
    }
    return null;
}

function lineItemFromTemplate(template, home, site) {
    const requiresBid = Boolean(template.requires_bid || template.calc_type === "bid_required");
    const description = template.description || template.name || "Service item";
    const calculated = calculatedLinePricing(template, home, site);
    const templatePricingSource = template.source === "service_catalog" ? "CRM_Service_Catalog" : template.source === "legacy_templates" ? "Legacy quote template" : undefined;

    if (calculated) {
        const calculatedRequiresBid = Boolean(calculated.requiresBid);
        return {
            description: calculated.descriptionSuffix ? `${description} (${calculated.descriptionSuffix})` : description,
            qty: 1,
            unit_price: calculatedRequiresBid ? 0 : calculated.price,
            cost: calculatedRequiresBid ? 0 : calculated.cost,
            included_in_financing: template.included_in_financing !== false,
            vendor: template.vendor,
            pricing_type: template.pricing_type || template.pricingType || template.calc_type,
            catalog_template_id: template.source === "service_catalog" ? template.id : undefined,
            catalog_category: template.service_catalog_category,
            service_id: template.service_id,
            calc_type: template.calc_type || serviceKind(template),
            requires_bid: calculatedRequiresBid,
            unit: calculatedRequiresBid ? "bid_required" : "calculated",
            calculation_note: calculated.calcNote,
            pricing_source: calculated.pricingSource,
            calculation_snapshot: calculated,
        };
    }

    const qty = defaultTemplateQuantity(template, home, site);
    const unitPrice = templatePrice(template);
    const cost = templateCost(template);
    const bidRequired = requiresBid || unitPrice <= 0;

    return {
        description,
        qty,
        unit_price: bidRequired ? 0 : unitPrice,
        cost: bidRequired ? 0 : cost,
        included_in_financing: template.included_in_financing !== false,
        vendor: template.vendor,
        pricing_type: template.pricing_type || template.pricingType || template.calc_type,
        catalog_template_id: template.source === "service_catalog" ? template.id : undefined,
        catalog_category: template.service_catalog_category,
        service_id: template.service_id,
        calc_type: template.calc_type,
        requires_bid: bidRequired,
        unit: bidRequired ? "bid_required" : template.unit,
        calculation_note: bidRequired ? "No confirmed table/rate/retail price found. Requires bid before final quote." : undefined,
        pricing_source: templatePricingSource,
    };
}

function pushUniqueLineItem(groups, category, template, home, site, options = {}) {
    const targetCategory = options.categoryOverride || category;
    if (!template || !targetCategory || !groups[targetCategory]) return false;
    if (hasMatchingLineItem(groups, template, options.family)) return false;
    groups[targetCategory].push(lineItemFromTemplate(template, home, site));
    return true;
}

function buildAutomaticServiceLines({ quote, templates }) {
    const home = quote.home || {};
    const site = quote.site || {};
    const next = {
        mandatory_services: [...(quote.mandatory_services || [])],
        addons: [...(quote.addons || [])],
        site_work: [...(quote.site_work || [])],
    };
    const missingRequiredTemplates = [];
    const suppressed = new Set(quote.suppressed_required_service_ids || []);

    for (const serviceName of DEFAULT_AUTO_SERVICES) {
        if (serviceName === "Dirt Pad" && site.dirt_pad_needed === false) continue;
        const template = findTemplate(templates, [serviceName]);
        pushUniqueLineItem(next, template?.quote_category, template, home, site);
    }

    const permitTemplate = findPermitTemplate(templates);
    if (permitTemplate && !suppressed.has("SITE-PERMIT-PLAN")) {
        pushUniqueLineItem(next, "mandatory_services", permitTemplate, home, site, { family: "permit", categoryOverride: "mandatory_services" });
    } else if (!existingLineItems(next).some(isPermitLine)) {
        missingRequiredTemplates.push("permit");
    }

    const stepTemplates = findStepTemplates(templates);
    if (stepTemplates.length > 0 && !suppressed.has("SITE-STEPS-2SET")) {
        for (const stepTemplate of stepTemplates) {
            pushUniqueLineItem(next, "mandatory_services", stepTemplate, home, site, { family: "steps", categoryOverride: "mandatory_services" });
        }
    } else if (!existingLineItems(next).some(isStepsLine)) {
        missingRequiredTemplates.push("steps");
    }

    if (site.water_source === "well" || !site.water_source) {
        const well = findTemplate(templates, ["Well System"]);
        pushUniqueLineItem(next, well?.quote_category, well, home, site);
    } else if (site.water_source === "city_water" || site.water_source === "existing") {
        const water = findTemplate(templates, ["Water Hookups", "Water Hookup"]);
        pushUniqueLineItem(next, water?.quote_category, water, home, site);
    }

    if (site.sewer_source === "septic" || !site.sewer_source) {
        const septic = findTemplate(templates, ["Septic System"]);
        pushUniqueLineItem(next, septic?.quote_category, septic, home, site);
    } else if (site.sewer_source === "city_sewer" || site.sewer_source === "existing") {
        const sewer = findTemplate(templates, ["Sewer Hookups", "Sewer Hookup"]);
        pushUniqueLineItem(next, sewer?.quote_category, sewer, home, site);
    }

    if (site.skirting_needed !== false) {
        const skirting = findTemplate(templates, ["Skirting Basic Valor", "Skirting"]);
        pushUniqueLineItem(next, skirting?.quote_category, skirting, home, site);
    }

    if (site.gutters_needed) {
        const gutters = findTemplate(templates, ["Gutters & Downspouts", "Gutters"]);
        pushUniqueLineItem(next, gutters?.quote_category, gutters, home, site);
    }

    const delivery = findTemplate(templates, ["Delivery"]);
    pushUniqueLineItem(next, delivery?.quote_category, delivery, home, site);

    const freight = findTemplate(templates, ["Freight"]);
    pushUniqueLineItem(next, freight?.quote_category, freight, home, site);

    if (site.driveway_needed) {
        const driveway = findTemplate(templates, ["Driveway"]);
        pushUniqueLineItem(next, driveway?.quote_category, driveway, home, site);
    }

    if (site.deck_needed) {
        const deck = findTemplate(templates, ["Deck"]);
        pushUniqueLineItem(next, deck?.quote_category, deck, home, site);
    }

    if (site.porch_needed) {
        const porch = findTemplate(templates, ["Porch"]);
        pushUniqueLineItem(next, porch?.quote_category, porch, home, site);
    }

    if (site.demolition_needed) {
        const demolition = findTemplate(templates, ["Demolition & Removal", "Demolition"]);
        pushUniqueLineItem(next, demolition?.quote_category, demolition, home, site);
    }

    if (site.land_clearing_needed) {
        const clearing = findTemplate(templates, ["Land Clearing"]);
        pushUniqueLineItem(next, clearing?.quote_category, clearing, home, site);
    }

    if (site.landscaping_needed) {
        const landscaping = findTemplate(templates, ["Landscaping"]);
        pushUniqueLineItem(next, landscaping?.quote_category, landscaping, home, site);
    }

    return { lines: next, missingRequiredTemplates };
}

function LineItemTable({ category, items, onChange, onRemove, onAdd, templates, home, site }) {
    const setItem = (idx, patch) => {
        const next = items.slice();
        next[idx] = { ...next[idx], ...patch, user_modified: true };
        onChange(next);
    };

    const remove = (idx) => onRemove(idx, items[idx]);
    const addBlank = () => onChange([...items, { description: "", qty: 1, unit_price: 0, cost: 0 }]);

    const addTemplate = (tplId) => {
        const tpl = templates.find((t) => t.id === tplId);
        if (!tpl) return;
        const line = lineItemFromTemplate(tpl, home, site);
        onAdd([...items, line], line);
    };

    return (
        <div className="space-y-3">
            {items.length > 0 && (
                <div className="overflow-x-auto rounded-lg border border-slate-200">
                    <div className="min-w-[760px]">
                        <div className="grid grid-cols-12 bg-slate-50 px-3 py-2 text-[11px] uppercase tracking-wider text-slate-500">
                            <div className="col-span-5">Description</div>
                            <div className="col-span-1 text-right">Qty</div>
                            <div className="col-span-2 text-right">Unit Price</div>
                            <div className="col-span-2 text-right">Cost (internal)</div>
                            <div className="col-span-1 text-center">Financed</div>
                            <div className="col-span-1 text-right">Amount</div>
                            <div className="col-span-1" />
                        </div>
                        {items.map((it, i) => (
                            <div key={i} className="grid grid-cols-12 items-center border-t border-slate-100 px-3 py-2">
                                <div className="col-span-5">
                                    <Input
                                        value={it.description}
                                        onChange={(e) => setItem(i, { description: e.target.value })}
                                        placeholder="Description"
                                        className="h-9"
                                        data-testid={`step-pricing-${category}-desc-${i}`}
                                    />
                                    {(it.calc_type || it.unit || it.requires_bid || it.calculation_note || it.pricing_source) && (
                                        <div className="mt-1 text-[11px] text-slate-500">
                                            {[it.requires_bid ? "BID REQUIRED" : null, it.calc_type, it.unit, it.pricing_source].filter(Boolean).join(" · ")}
                                            {it.calculation_note && <div>{it.calculation_note}</div>}
                                        </div>
                                    )}
                                </div>
                                <div className="col-span-1">
                                    <Input
                                        type="number"
                                        step="1"
                                        value={it.qty}
                                        onChange={(e) => setItem(i, { qty: parseFloat(e.target.value || 0) })}
                                        className="h-9 text-right tabular"
                                        data-testid={`step-pricing-${category}-qty-${i}`}
                                    />
                                </div>
                                <div className="col-span-2">
                                    <Input
                                        type="number"
                                        step="0.01"
                                        value={it.unit_price}
                                        onChange={(e) => setItem(i, { unit_price: parseFloat(e.target.value || 0) })}
                                        className="h-9 text-right tabular"
                                        data-testid={`step-pricing-${category}-price-${i}`}
                                    />
                                </div>
                                <div className="col-span-2">
                                    <Input
                                        type="number"
                                        step="0.01"
                                        value={it.cost || 0}
                                        onChange={(e) => setItem(i, { cost: parseFloat(e.target.value || 0) })}
                                        className="h-9 text-right tabular"
                                    />
                                </div>
                                <div className="col-span-1 flex justify-center">
                                    <Checkbox
                                        checked={it.included_in_financing !== false}
                                        onCheckedChange={(checked) => setItem(i, { included_in_financing: checked === true })}
                                        aria-label="Included in financing"
                                        data-testid={`step-pricing-${category}-financed-${i}`}
                                    />
                                </div>
                                <div className="col-span-1 text-right text-sm tabular text-slate-900">
                                    {money((it.qty || 0) * (it.unit_price || 0))}
                                </div>
                                <div className="col-span-1 text-right">
                                    <button
                                        onClick={() => remove(i)}
                                        className="text-slate-400 transition-colors hover:text-red-600"
                                        data-testid={`step-pricing-${category}-remove-${i}`}
                                    >
                                        <Trash2 className="h-4 w-4" />
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}
            <div className="flex min-w-0 flex-col gap-2 sm:flex-row sm:items-center">
                <Button variant="outline" size="sm" onClick={addBlank} data-testid={`step-pricing-${category}-add-blank`} className="w-full shrink-0 sm:w-auto">
                    <Plus className="mr-1 h-4 w-4" /> Add row
                </Button>
                {templates && templates.length > 0 && (
                    <Select onValueChange={addTemplate}>
                        <SelectTrigger className="h-9 w-full min-w-0 sm:max-w-[300px] md:max-w-[360px]" data-testid={`step-pricing-${category}-template-select`}>
                            <SelectValue placeholder="+ Add from service catalog…" />
                        </SelectTrigger>
                        <SelectContent className="max-h-[420px] w-[min(680px,calc(100vw-2rem))] max-w-[calc(100vw-2rem)]">
                            {templates.map((t) => (
                                <SelectItem key={t.id} value={t.id} className="py-2 pr-2">
                                    <span className="grid w-full grid-cols-[minmax(0,1fr)_auto] items-start gap-4">
                                        <span className="min-w-0">
                                            <span className="block whitespace-normal break-words leading-snug" title={t.description || t.name}>
                                                {t.description || t.name}
                                            </span>
                                            {(t.calc_type || t.unit || t.service_catalog_category) && (
                                                <span className="mt-0.5 block text-[11px] text-slate-500">
                                                    {[t.service_catalog_category, t.calc_type, t.unit].filter(Boolean).join(" · ")}
                                                </span>
                                            )}
                                        </span>
                                        <span className="shrink-0 tabular-nums text-slate-500">{templatePriceLabel(t)}</span>
                                    </span>
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                )}
            </div>
        </div>
    );
}

function Discounts({ items, onChange }) {
    const set = (idx, patch) => {
        const n = items.slice();
        n[idx] = { ...n[idx], ...patch };
        onChange(n);
    };
    const add = () => onChange([...items, { description: "", amount: 0 }]);
    const remove = (idx) => onChange(items.filter((_, i) => i !== idx));
    return (
        <div className="space-y-2">
            {items.map((d, i) => (
                <div key={i} className="flex min-w-0 flex-col gap-2 sm:flex-row sm:items-center">
                    <Input value={d.description} onChange={(e) => set(i, { description: e.target.value })} placeholder="Discount description" className="min-w-0 flex-1" />
                    <Input type="number" step="0.01" value={d.amount} onChange={(e) => set(i, { amount: parseFloat(e.target.value || 0) })} className="text-right tabular sm:w-32" />
                    <button onClick={() => remove(i)} className="self-end text-slate-400 hover:text-red-600 sm:self-auto"><Trash2 className="h-4 w-4" /></button>
                </div>
            ))}
            <Button variant="outline" size="sm" onClick={add} data-testid="step-pricing-add-discount"><Plus className="mr-1 h-4 w-4" /> Add discount</Button>
        </div>
    );
}

export default function StepPricing({ quote, update, settings }) {
    const [templates, setTemplates] = useState(EMPTY_TEMPLATE_GROUPS);
    const [templateSource, setTemplateSource] = useState("loading");
    const [syncingCatalog, setSyncingCatalog] = useState(false);

    const loadTemplates = useCallback(async () => {
        setTemplateSource("loading");
        try {
            const serviceCatalogResult = await api.get("/service-catalog");
            const normalized = (serviceCatalogResult.data || []).map(normalizeServiceCatalogTemplate).filter(Boolean);

            if (normalized.length > 0) {
                setTemplates(groupTemplates(normalized));
                setTemplateSource("service_catalog");
                return;
            }
        } catch (serviceCatalogError) {
            // Fall back to the older template table until the service catalog endpoint is live/synced.
        }

        try {
            const legacyResult = await api.get("/templates");
            const normalized = (legacyResult.data || []).map(normalizeLegacyTemplate).filter(Boolean);
            setTemplates(groupTemplates(normalized));
            setTemplateSource("legacy_templates");
        } catch (legacyError) {
            setTemplates(EMPTY_TEMPLATE_GROUPS);
            setTemplateSource("unavailable");
        }
    }, []);

    useEffect(() => {
        loadTemplates();
    }, [loadTemplates]);

    const syncServiceCatalog = async () => {
        setSyncingCatalog(true);
        try {
            const result = await api.post("/service-catalog/sync");
            await loadTemplates();
            toast.success(`Service catalog synced: ${result.data?.synced_count || 0} items`);
        } catch (err) {
            toast.error(getApiErrorMessage(err, "Service catalog sync failed"));
        } finally {
            setSyncingCatalog(false);
        }
    };

    const autoBuildServices = () => {
        if (templateSource === "loading") {
            toast.error("Service catalog is still loading");
            return;
        }

        const { lines, missingRequiredTemplates } = buildAutomaticServiceLines({ quote, templates });
        update(lines);
        if (missingRequiredTemplates.length > 0) {
            toast.warning("Permit/Steps pricing template not found. Check service catalog import.");
        } else {
            toast.success("Service lines auto-built from selected home and site inputs");
        }
    };

    const updateLineItems = (category, items) => update({ [category]: items });
    const removeLineItem = (category, items, index, item) => {
        const patch = { [category]: items.filter((_, i) => i !== index) };
        if (DEFAULT_SERVICE_IDS.has(item?.service_id)) {
            patch.suppressed_required_service_ids = [...new Set([
                ...(quote.suppressed_required_service_ids || []),
                item.service_id,
            ])];
        }
        update(patch);
    };
    const addLineItem = (category, items, item) => {
        const patch = { [category]: items };
        if (DEFAULT_SERVICE_IDS.has(item?.service_id)) {
            patch.suppressed_required_service_ids = (quote.suppressed_required_service_ids || [])
                .filter((serviceId) => serviceId !== item.service_id);
        }
        update(patch);
    };

    const sourceLabel = {
        loading: "Loading catalog…",
        service_catalog: "Using synced CRM_Service_Catalog",
        legacy_templates: "Using legacy line item templates",
        unavailable: "No service catalog available",
    }[templateSource];

    const calculatedMetrics = useMemo(() => {
        const ac = calculateAcPricing(quote.home, quote.site || {});
        const block = calculateBlockTieDownPricing(quote.home || {});
        const dirtPad = calculateDirtPadPricing(quote.site || {});
        const delivery = calculateDeliveryPricing(quote.home || {}, quote.site || {}, "dealer_to_customer");
        const freight = calculateDeliveryPricing(quote.home || {}, quote.site || {}, "factory_to_dealer");
        return {
            perimeter: homePerimeter(quote.home),
            longSides: homeLongSides(quote.home),
            length: homeLength(quote.home),
            acTonnage: ac.tonnage,
            acPrice: ac.price,
            blockPrice: block.price,
            dirtPadPrice: dirtPad.price,
            dirtPadLoads: dirtPad.loads,
            dirtPadRequiresBid: dirtPad.requiresBid,
            deliveryPrice: delivery.price,
            freightPrice: freight.price,
        };
    }, [quote.home, quote.site]);

    return (
        <div className="space-y-4" data-testid="step-pricing">
            <Card className="rounded-xl border-slate-200 bg-slate-50/70 p-4">
                <div className="flex flex-wrap items-center justify-between gap-3">
                    <div>
                        <div className="text-sm font-semibold text-slate-900">Service catalog source</div>
                        <div className="mt-1 text-xs text-slate-500">
                            {sourceLabel}. Home base prices remain controlled by the Home Catalog.
                        </div>
                        <div className="mt-2 flex flex-wrap gap-2 text-[11px] text-slate-500">
                            <span>Perimeter: {calculatedMetrics.perimeter} ft</span>
                            <span>Long sides: {calculatedMetrics.longSides} ft</span>
                            <span>Length: {calculatedMetrics.length} ft</span>
                            <span>AC: {calculatedMetrics.acTonnage} ton / {money(calculatedMetrics.acPrice)}</span>
                            <span>Block/tie: {money(calculatedMetrics.blockPrice)}</span>
                            <span>Dirt pad: {calculatedMetrics.dirtPadRequiresBid ? "BID" : money(calculatedMetrics.dirtPadPrice)} / {calculatedMetrics.dirtPadLoads || 0} load(s)</span>
                            <span>Delivery: {money(calculatedMetrics.deliveryPrice)}</span>
                            <span>Freight: {money(calculatedMetrics.freightPrice)}</span>
                        </div>
                    </div>
                    <div className="flex flex-wrap gap-2">
                        <Button variant="outline" size="sm" onClick={loadTemplates}>
                            <RefreshCcw className="mr-1.5 h-4 w-4" /> Reload
                        </Button>
                        <Button variant="outline" size="sm" onClick={syncServiceCatalog} disabled={syncingCatalog}>
                            <RefreshCcw className="mr-1.5 h-4 w-4" /> {syncingCatalog ? "Syncing…" : "Sync sheet"}
                        </Button>
                        <Button size="sm" onClick={autoBuildServices} className="bg-[#0F2A47] text-white hover:bg-[#14385E]">
                            <Wand2 className="mr-1.5 h-4 w-4" /> Auto-build services
                        </Button>
                    </div>
                </div>
            </Card>

            {CATEGORIES.map((cat) => (
                <Card key={cat.key} className="space-y-3 rounded-xl border-slate-200 p-5">
                    <div className="flex flex-wrap items-end justify-between gap-4">
                        <div>
                            <div className="text-sm font-semibold text-slate-900">{cat.title}</div>
                            <div className="text-xs text-slate-500">{cat.description}</div>
                        </div>
                    </div>
                    <LineItemTable
                        category={cat.key}
                        items={quote[cat.key] || []}
                        onChange={(items) => updateLineItems(cat.key, items)}
                        onRemove={(index, item) => removeLineItem(cat.key, quote[cat.key] || [], index, item)}
                        onAdd={(items, item) => addLineItem(cat.key, items, item)}
                        templates={templates[cat.key] || []}
                        home={quote.home}
                        site={quote.site || {}}
                    />
                </Card>
            ))}

            <Card className="space-y-3 rounded-xl border-slate-200 p-5">
                <div className="flex flex-wrap items-end justify-between gap-4">
                    <div>
                        <div className="text-sm font-semibold text-slate-900">Discounts & adjustments</div>
                        <div className="text-xs text-slate-500">Subtracted from subtotal before tax</div>
                    </div>
                    {settings && (
                        <div className="text-xs text-slate-500">
                            Tax rate: {((quote.sales_tax_rate ?? settings.sales_tax_rate) * 100).toFixed(2)}%
                        </div>
                    )}
                </div>
                <Discounts items={quote.discounts || []} onChange={(d) => update({ discounts: d })} />
            </Card>
        </div>
    );
}
